<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { ContractGenerationTemplateFieldInDb } from '@/api';
import type { FieldTreeModel, FieldTreeRow } from '@/utils/contractGeneration/templateFieldTree';
import { FIELD_TYPE_LABEL, FIELD_TYPE_TAG } from '@/utils/contractGeneration/templateSchemaFields';
import { humanizeKey, lastPathSegment } from '@/utils/contractGeneration/templateFieldLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaReorderRow from '@/components/design/composite/GaiaReorderRow.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

/**
 * The left half of the field-guidance page: every field of the template as a
 * tree, one row per path, with the fields whose tag has gone listed under it.
 * Rows can be dragged within their own sibling group — the same split as the
 * kanban board, where the row draws the grip and this list owns the gesture.
 */
interface Props {
  model: FieldTreeModel;
  selectedPath: string | null;
  /** The label a row shows — the author's draft label, else the fallback. */
  resolveLabel: (row: FieldTreeRow) => string;
  /** Whether the draft holds any writing for this path. */
  hasNotes: (path: string) => boolean;
  isDirtyPath: (path: string) => boolean;
  /** False while the schema is unreadable — order can't be trusted then. */
  canReorder: boolean;
  /** Orphan path → the live path its notes were moved onto this session. */
  adoptedOrphans: Map<string, string>;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  select: [path: string];
  reorder: [orderedPaths: string[]];
}>();

// --- expansion --------------------------------------------------------------
// Default is fully expanded: an author wants to see everything at once, and a
// long template can be folded from the header.

const expandedPaths = ref<Set<string>>(new Set());

const parents = computed<FieldTreeRow[]>(() => props.model.rows.filter(row => row.hasChildren));

watch(
  () => props.model.rows,
  rows => {
    const next = new Set<string>();
    for (const row of rows) if (row.hasChildren) next.add(row.path);
    expandedPaths.value = next;
  },
  { immediate: true }
);

const allExpanded = computed<boolean>(() =>
  parents.value.every(row => expandedPaths.value.has(row.path))
);

function toggleExpanded(path: string): void {
  const next = new Set(expandedPaths.value);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  expandedPaths.value = next;
}

function toggleAll(): void {
  expandedPaths.value = allExpanded.value ? new Set() : new Set(parents.value.map(row => row.path));
}

/**
 * Skips rows inside a collapsed subtree. The list is in pre-order, so once a
 * collapsed row is passed, every deeper row until the next row at its depth
 * or shallower belongs to it.
 */
const visibleRows = computed<FieldTreeRow[]>(() => {
  const out: FieldTreeRow[] = [];
  let collapsedAtDepth: number | null = null;
  for (const row of props.model.rows) {
    if (collapsedAtDepth !== null && row.depth > collapsedAtDepth) continue;
    collapsedAtDepth = null;
    out.push(row);
    if (row.hasChildren && !expandedPaths.value.has(row.path)) collapsedAtDepth = row.depth;
  }
  return out;
});

// --- reordering ---------------------------------------------------------------
//
// The gesture reads like a sortable list: while a row is carried, an accent
// line marks the gap it would drop into; on drop the siblings slide into their
// new places and the landed row glows for a moment.
//
// The whole pane is the drop zone, not the rows. Listening on rows leaves dead
// zones — the gaps between them, the padding under the last one, the empty
// pane below the list — where a release does nothing, and "drag the first
// field to the bottom" lands there more often than not. Instead the gap is
// read from the pointer's height against the carried row's siblings: above a
// sibling's midpoint means before it, past the last one means after it.

interface DropTarget {
  path: string;
  position: 'before' | 'after';
}

const draggedPath = ref<string | null>(null);
const dropTarget = ref<DropTarget | null>(null);
/** The row that just landed — highlighted briefly so the eye finds it. */
const settledPath = ref<string | null>(null);
let settledTimer: ReturnType<typeof setTimeout> | null = null;

const draggedRow = computed<FieldTreeRow | null>(() =>
  draggedPath.value ? (props.model.byPath.get(draggedPath.value) ?? null) : null
);

function siblingsOf(row: FieldTreeRow): FieldTreeRow[] {
  return props.model.rows.filter(candidate => candidate.parentPath === row.parentPath);
}

function isLocked(row: FieldTreeRow): boolean {
  return !props.canReorder || siblingsOf(row).length < 2;
}

function clearDrag(): void {
  draggedPath.value = null;
  dropTarget.value = null;
}

function markSettled(path: string): void {
  if (settledTimer) clearTimeout(settledTimer);
  settledPath.value = path;
  settledTimer = setTimeout(() => {
    settledPath.value = null;
    settledTimer = null;
  }, 700);
}

onBeforeUnmount(() => {
  if (settledTimer) clearTimeout(settledTimer);
});

function handleDragStart(event: DragEvent, row: FieldTreeRow): void {
  if (isLocked(row)) {
    event.preventDefault();
    return;
  }
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', row.path);
  }
  // Writing reactive state inside dragstart re-renders the dragged element
  // mid-gesture and Chromium cancels the drag — defer past the event.
  setTimeout(() => {
    draggedPath.value = row.path;
  }, 0);
}

/**
 * The group's paths with the carried row moved into the gap, renumbered from
 * that order so the positions written are absolute — or null when the gap is
 * where the row already sits.
 */
function reorderedPaths(dragged: FieldTreeRow, target: DropTarget): string[] | null {
  const current = siblingsOf(dragged).map(sibling => sibling.path);
  const ordered = current.filter(path => path !== dragged.path);
  const anchor = ordered.indexOf(target.path);
  if (anchor === -1) return null;
  ordered.splice(target.position === 'before' ? anchor : anchor + 1, 0, dragged.path);
  return ordered.some((path, index) => path !== current[index]) ? ordered : null;
}

/**
 * Which gap the pointer is over, read against the carried row's siblings in
 * display order: before the first sibling whose midpoint is still below the
 * pointer, else after the last. Rows of other groups in between (a sibling's
 * children) fall into the same gaps, so nothing under the pointer is dead.
 */
function gapUnderPointer(
  container: HTMLElement,
  dragged: FieldTreeRow,
  clientY: number
): DropTarget | null {
  const elements = new Map<string, HTMLElement>();
  for (const element of container.querySelectorAll<HTMLElement>('[data-path]')) {
    if (element.dataset.path) elements.set(element.dataset.path, element);
  }
  const candidates = siblingsOf(dragged).filter(sibling => sibling.path !== dragged.path);
  for (const candidate of candidates) {
    const element = elements.get(candidate.path);
    if (!element) continue;
    const rect = element.getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) return { path: candidate.path, position: 'before' };
  }
  const last = candidates.at(-1);
  return last ? { path: last.path, position: 'after' } : null;
}

function handleTreeDragOver(event: DragEvent): void {
  const dragged = draggedRow.value;
  const container = event.currentTarget;
  if (!dragged || !(container instanceof HTMLElement)) return;
  const target = gapUnderPointer(container, dragged, event.clientY);
  if (!target) return;
  // Accept the drop wherever the pointer is; only draw the line when it would
  // actually move the row.
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  const next = reorderedPaths(dragged, target) ? target : null;
  if (dropTarget.value?.path !== next?.path || dropTarget.value?.position !== next?.position) {
    dropTarget.value = next;
  }
}

function handleTreeDrop(event: DragEvent): void {
  event.preventDefault();
  const dragged = draggedRow.value;
  const target = dropTarget.value;
  clearDrag();
  if (!dragged || !target) return;
  const ordered = reorderedPaths(dragged, target);
  if (!ordered) return;
  emit('reorder', ordered);
  markSettled(dragged.path);
}

function dropClass(row: FieldTreeRow): Record<string, boolean> {
  const target = dropTarget.value;
  const isTarget = target !== null && target.path === row.path && draggedPath.value !== row.path;
  return {
    'drop-before': isTarget && target.position === 'before',
    'drop-after': isTarget && target.position === 'after',
    settled: settledPath.value === row.path,
  };
}

// --- orphans ------------------------------------------------------------------

function orphanLabel(orphan: ContractGenerationTemplateFieldInDb): string {
  const label = orphan.label?.trim();
  if (label) return label;
  return humanizeKey(lastPathSegment(orphan.path));
}

function adoptedTargetLabel(orphan: ContractGenerationTemplateFieldInDb): string | null {
  const targetPath = props.adoptedOrphans.get(orphan.path);
  if (!targetPath) return null;
  const target = props.model.byPath.get(targetPath);
  return target ? props.resolveLabel(target) : targetPath;
}

// --- chrome ---------------------------------------------------------------------
const divider = gaiaNeutral.borderDivider;
const chevron = gaiaNeutral.textSubtle;
const dropLine = gaiaAccent.base;
const settleTint = gaiaAccent.wash;
</script>

<template>
  <div class="field-tree" @dragover="handleTreeDragOver" @drop="handleTreeDrop">
    <n-flex align="center" :size="8" :wrap="false" class="tree-head">
      <gaia-text size="dense" weight="semibold" tone="ink">Fields</gaia-text>
      <gaia-count-pill>{{ model.rows.length }}</gaia-count-pill>
      <gaia-button
        v-if="parents.length > 0"
        size="tiny"
        quaternary
        :style="{ marginLeft: 'auto' }"
        @click="toggleAll"
      >
        {{ allExpanded ? 'Collapse all' : 'Expand all' }}
      </gaia-button>
    </n-flex>

    <n-flex v-if="model.rows.length === 0" justify="center" :style="{ padding: '32px 16px' }">
      <gaia-empty-state
        dashed
        :title="
          model.schemaAvailable
            ? 'No fields in this template'
            : 'Fields appear once validation finishes'
        "
      >
        {{
          model.schemaAvailable
            ? 'The document has no placeholders to describe yet.'
            : 'The template is being checked. The list fills in on its own.'
        }}
      </gaia-empty-state>
    </n-flex>

    <!-- A transition group so rows slide into place after a drop. -->
    <transition-group v-else name="tree-row" tag="div" class="tree-body">
      <div
        v-for="row in visibleRows"
        :key="row.path"
        class="tree-row"
        :class="dropClass(row)"
        :data-path="row.path"
        :style="{ paddingLeft: `${row.depth * 18}px`, '--indent': `${row.depth * 18 + 8}px` }"
      >
        <gaia-reorder-row
          :label="resolveLabel(row)"
          :meta="row.path"
          :locked="isLocked(row)"
          :selected="row.path === selectedPath"
          :dragging="draggedPath === row.path"
          :style="{ cursor: 'pointer' }"
          @click="emit('select', row.path)"
          @dragstart="(event: DragEvent) => handleDragStart(event, row)"
          @dragend="clearDrag"
        >
          <template #badges>
            <gaia-tag
              v-if="row.schema"
              size="tiny"
              round
              :type="FIELD_TYPE_TAG[row.schema.type] ?? 'default'"
            >
              {{ FIELD_TYPE_LABEL[row.schema.type] ?? row.schema.type }}
            </gaia-tag>
            <gaia-status-indicator
              v-if="isDirtyPath(row.path)"
              status="warning"
              glyph="dot"
              title="Unsaved changes"
            />
            <gaia-status-indicator
              v-else-if="hasNotes(row.path)"
              status="accent"
              glyph="dot"
              title="Has guidance"
            />
          </template>
          <template v-if="row.hasChildren" #actions>
            <gaia-icon-button
              quaternary
              size="tiny"
              :dimension="22"
              :aria-label="expandedPaths.has(row.path) ? 'Collapse' : 'Expand'"
              @click.stop="toggleExpanded(row.path)"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                class="chevron"
                :class="{ open: expandedPaths.has(row.path) }"
              >
                <path d="M4.5 2.5 8 6l-3.5 3.5" />
              </svg>
            </gaia-icon-button>
          </template>
        </gaia-reorder-row>
      </div>
    </transition-group>

    <template v-if="model.orphans.length > 0">
      <n-flex align="center" :size="8" :wrap="false" class="tree-head orphans">
        <gaia-status-indicator status="warning" glyph="dot" />
        <gaia-text size="dense" weight="semibold" tone="ink">No longer in the document</gaia-text>
        <gaia-count-pill>{{ model.orphans.length }}</gaia-count-pill>
      </n-flex>
      <div class="tree-body">
        <gaia-reorder-row
          v-for="orphan in model.orphans"
          :key="orphan.path"
          locked
          :label="orphanLabel(orphan)"
          :meta="orphan.path"
          :selected="orphan.path === selectedPath"
          :style="{ cursor: 'pointer' }"
          @click="emit('select', orphan.path)"
        >
          <template v-if="adoptedTargetLabel(orphan)" #badges>
            <gaia-tag size="tiny" variant="muted" round>
              Moved to {{ adoptedTargetLabel(orphan) }}
            </gaia-tag>
          </template>
        </gaia-reorder-row>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* A list with a pinned head: the head's rule and the body's inset are the
   list's own geometry, not something a component expresses. */
/* The tree fills its pane so the space under a short list is a drop zone too. */
.field-tree {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.tree-head {
  flex: none;
  padding: 12px 14px 8px;
}

.tree-head.orphans {
  margin-top: 8px;
  border-top: 1px solid v-bind(divider);
}

.tree-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 8px 10px;
}

/* The drag feedback is drawn by the list, not the row: an insertion line in
   the gap the drop would fill, a slide when the order changes, a glow on the
   row that landed. Pseudo-elements and keyframes have no component form. */
.tree-row {
  position: relative;
  border-radius: 8px;
}

.tree-row.drop-before::before,
.tree-row.drop-after::after {
  content: '';
  position: absolute;
  left: var(--indent);
  right: 8px;
  height: 2px;
  border-radius: 1px;
  background: v-bind(dropLine);
  pointer-events: none;
}

.tree-row.drop-before::before {
  top: -2px;
}

.tree-row.drop-after::after {
  bottom: -2px;
}

.tree-row-move {
  transition: transform 0.22s cubic-bezier(0.2, 0, 0, 1);
}

.tree-row.settled {
  animation: tree-row-settle 0.7s ease-out;
}

@keyframes tree-row-settle {
  from {
    background: v-bind(settleTint);
  }
  to {
    background: transparent;
  }
}

.chevron {
  color: v-bind(chevron);
  transition: transform 0.15s;
}

.chevron.open {
  transform: rotate(90deg);
}
</style>
